import React, { FC, Component, MouseEvent } from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';

import i18n, { translations } from 'i18n';

import { removeFavorite } from 'actions/registry';
import { uninstallApp } from 'actions/gallery';

import App from 'models/app';

import Modal from './Modal';
import AppCarousel from '../AppCarousel/index';
import StateManagementButton from '../StateManagementButton/index';
import ButtonBar from '../Button/ButtonBar';
import PremiumSubscribeButton, { PremiumSubscribeButtonType } from '../Premium/PremiumSubscribeButton';
import { LinkAction, ActionsContainer } from '../LinkAction/index';
import { AutomationText } from '../Automation';
import { PortalContainer } from '../Portal/common';
import { FontIcon } from '../FontIcon';
import Typography from '../Typography';

import premiumLabelIcon from 'icons/premium-logo-text.svg';

interface ClockDetailsProps {
  show: boolean;
  clock: App;
  deviceName: string;
  favoriteClocksEnabled: boolean;
  favoriteClocksCount?: number;
  installedClocksCount: number;
  maxFavoriteClocks: number;
  removeLoading: boolean;
  userHasGalleryGrant: boolean;
  removeFavorite: () => any;
  setFocusedClock: (id: string | null) => any;
  isFirstClock: boolean;
}

const FULL_VH = document.documentElement.clientHeight;

const ScrollableContainer = styled.div`
  overflow-y: auto;
  height: ${FULL_VH - 50}px;
  overflow-x: hidden;
`;

const StyledActionsContainer = styled(ActionsContainer)`
  margin-bottom: 22px;
  padding: 0 24px;
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  height: 64px;
  position: relative;
  background-color: var(--sys-color--surface);
  color: var(--sys-color--on-surface);
  border-bottom: 1px solid ${({ theme }) => theme.borderColorV2 || '#E7E9EA'};
`;

const AppName = styled(Typography)`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  max-width: 60%;
`;

const CloseButtonDiv = styled.div`
  position: absolute;
  right: 0;
  display: flex;
  align-items: center;
  padding: 0 16px;
  cursor: pointer;
`;

const DevName = styled.div`
  width: 100%;
  text-align: center;
  margin-bottom: 3px;

  font-size: 13px;
  line-height: 16px;
  letter-spacing: -0.32px;

  color: #8f8f8f;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  box-sizing: border-box;
  padding: 16px;
  gap: 16px;
  align-items: center;
  width: 100%;
  flex-wrap: wrap;

  button, a {
    flex: 1 1 calc(50% - 8px);
    min-width: calc(50% - 8px);
  }

  a {
    display: block;

    button {
      flex: none;
      min-width: 0;
      width: 100%;
    }
  }
`;

const PremiumLabel = styled.div`
  background: url(${premiumLabelIcon}) center center / contain no-repeat;
  width: 75px;
  height: 14px;
  margin: auto;
`;

const SettingsButton: FC<any> = (props) => {
  const {
    component: {
      AppDetails: { customizeButtonText }
    }
  } = translations;

  return (
    <ButtonBar fullWidth variant='outline' onClick={() => props.app.navigateToSettings()}>{customizeButtonText()}</ButtonBar>
  );
};

const SelectButton: FC<any> = (props) => {
  const {
    page: {
      Clock: { InstallButtonState }
    }
  } = translations;

  const installTexts = Object.keys(InstallButtonState).reduce((texts: any, key) => {
    texts[key] = InstallButtonState[key]();
    return texts;
  }, {});

  return (
    <ButtonContainer>
      {props.app.isPremium && !props.userHasGalleryGrant
        ? <PremiumSubscribeButton
          trigger={`app-${props.app.id}`}
          type={PremiumSubscribeButtonType.INSTALL_BUTTON}
        />
        : <StateManagementButton texts={installTexts} app={props.app} isContents />
      }
      {props.app.isClock && props.app.isOnDevice && props.app.hasSettings ? <SettingsButton app={props.app}></SettingsButton> : null}
    </ButtonContainer>
  );
};

class ManageFavoriteAction extends Component<any> {
  state = {
    open: false
  };
  onOpen = () =>
    this.setState({
      open: true
    });

  onClose = () =>
    this.setState({
      open: false
    });

  render() {
    const { clock, removeLoading, removeFavorite } = this.props;

    const {
      page: {
        ClockGallery: {
          MyClocks: { details }
        }
      }
    } = translations;

    const actionId = 'remove-clock-action';
    const mainButtonText = removeLoading
      ? details.removeLoading
      : details.removeButton;
    const modalText = details.removeModal;

    const buttons: any = [
      {
        text: modalText.button(),
        onClick: this.onClose,
        id: "cancel-remove-clock"
      },
      {
        text: modalText.confirmButton(),
        highlight: true,
        id: "confirm-remove-clock",
        onClick: () => {
          removeFavorite(clock.id);
          this.onClose();
        }
      }
    ];

    return (
      <div style={{ marginTop: 12 }}>
        <ButtonBar
          key={actionId}
          id={actionId}
          variant="outline"
          compact
          onClick={this.onOpen}
          disabled={removeLoading}
        >
          {mainButtonText()}
        </ButtonBar>
        {this.state.open && (
          <Modal
            title={modalText.title()}
            content={modalText.content()}
            buttons={buttons}
            onClose={this.onClose}
          />
        )}
      </div>
    );
  }
}

const mapStateToProps = ({ gallery, registry }: { gallery: any, registry: any }) => ({
  removeLoading: gallery.uninstallApp.loading || registry.removeFavorite.loading,
  userHasGalleryGrant: registry.galleryGrant.userHasGrant
});

const mapDispatchToProps = (dispatch: any, { clock }: { clock: any }) => ({
  removeFavorite: () => {
    if (!clock.selected && clock.isOnDevice) {
      return dispatch(uninstallApp(clock.id));
    }

    if (!clock.favorite) {
      return;
    }

    dispatch(removeFavorite(clock.id));
  }
});

const ClockDetails: FC<ClockDetailsProps> = ({
  show,
  clock,
  deviceName,
  favoriteClocksEnabled,
  installedClocksCount,
  maxFavoriteClocks,
  removeLoading,
  userHasGalleryGrant,
  removeFavorite,
  setFocusedClock,
}) => {
  const actions: any = [];
  if (clock) {
    if (clock.isInstalled && clock.hasSettings) {
      actions.push({
        id: 'settings-action',
        text: i18n('page.ClockGallery.MyClocks.details.settings')(),
        onClick: () => { clock.navigateToSettings(); }
      });
    }

    actions.push({
      id: 'all-details-action',
      text: i18n('page.ClockGallery.MyClocks.details.appDetailsButton')(),
      to: `/v2/clock/${clock.id}`
    });
  }

  const {
    component: {
      Ratings: { rateClockface }
    }
  } = translations;

  // don't remove clock if there is only one available or
  // if it's selected for install or active when there are more available
  const canRemoveClock = (maxFavoriteClocks > 1 && installedClocksCount > 1) && !(clock?.selected || clock?.active);
  const showRemoveButton = favoriteClocksEnabled && !clock?.isInstalling && canRemoveClock;

  return (
    <PortalContainer show={show}>
      {!!clock && (
        <React.Fragment>
          <HeaderContainer>
            <AppName variant="titleLarge">
              {clock.name}
            </AppName>
            <CloseButtonDiv
              id='close-clock-details'
              aria-label='close-clock-details'
              onClick={() => { setFocusedClock(null); }}
            >
              <FontIcon name="close" size={24} />
              <AutomationText />
            </CloseButtonDiv>
          </HeaderContainer>
          <DevName>
            {clock.developer.name}
          </DevName>
          <ScrollableContainer>
            <AppCarousel
              app={clock}
              appName={clock.name}
              developerName={clock.developer.name}
              deviceName={deviceName}
            />
            <SelectButton app={clock} userHasGalleryGrant={userHasGalleryGrant} />
            {clock.isPremium && <PremiumLabel />}
            <StyledActionsContainer>
              {actions.map((action: any, index: number) => (
                <LinkAction
                  key={action.id}
                  {...action}
                  last={index === actions.length - 1 &&
                    (!favoriteClocksEnabled || clock.isInstalling)}
                />
              ))}
              {showRemoveButton && (
                <ManageFavoriteAction
                  clock={clock}
                  removeLoading={removeLoading}
                  removeFavorite={removeFavorite}
                />
              )}
            </StyledActionsContainer>
          </ScrollableContainer>
        </React.Fragment>
      )}
    </PortalContainer>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(ClockDetails);
